import { RefObject, useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');

interface BackgroundState {
  ariaHidden: string | null;
  tabIndex: string | null;
  inert: boolean;
}

export const useModalFocusTrap = (
  containerRef: RefObject<HTMLElement>,
  isOpen: boolean,
  initialFocusRef?: RefObject<HTMLElement>,
) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!isOpen || !container) {
      return;
    }

    (initialFocusRef?.current ?? getFocusableElements(container)[0] ?? container).focus();

    const backgroundStates = new Map<HTMLElement, BackgroundState>();
    let activeBranch: HTMLElement = container;

    while (activeBranch.parentElement && activeBranch !== document.body) {
      const parent = activeBranch.parentElement;
      Array.from(parent.children).forEach((sibling) => {
        if (
          !(sibling instanceof HTMLElement)
          || sibling === activeBranch
          || sibling.hasAttribute('data-modal-backdrop')
        ) {
          return;
        }

        backgroundStates.set(sibling, {
          ariaHidden: sibling.getAttribute('aria-hidden'),
          tabIndex: sibling.getAttribute('tabindex'),
          inert: sibling.inert,
        });
        sibling.setAttribute('aria-hidden', 'true');
        sibling.setAttribute('tabindex', '-1');
        sibling.inert = true;
      });
      activeBranch = parent;
    }

    const focusFirst = () => {
      (initialFocusRef?.current ?? getFocusableElements(container)[0] ?? container).focus();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = getFocusableElements(container);
      if (focusableElements.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === firstElement || !container.contains(activeElement))) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && (activeElement === lastElement || !container.contains(activeElement))) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!(event.target instanceof Node) || !container.contains(event.target)) {
        focusFirst();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('focusin', handleFocusIn);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('focusin', handleFocusIn);
      backgroundStates.forEach((state, element) => {
        state.ariaHidden === null
          ? element.removeAttribute('aria-hidden')
          : element.setAttribute('aria-hidden', state.ariaHidden);
        state.tabIndex === null
          ? element.removeAttribute('tabindex')
          : element.setAttribute('tabindex', state.tabIndex);
        element.inert = state.inert;
      });
    };
  }, [containerRef, initialFocusRef, isOpen]);
};